import { Link } from "react-router-dom";
import { useState } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import ComicCard from "@/components/base/ComicCard";
import { categories, comics, faqs } from "@/mocks/comics";

function HeroSection() {
  return (
    <section className="relative h-[520px] md:h-[640px] flex items-center justify-center overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=A%20stylized%20artistic%20Korean%20manhwa%20inspired%20illustration%20with%20warm%20orange%20and%20deep%20navy%20color%20palette%2C%20abstract%20dynamic%20composition%20with%20flowing%20ink%20brush%20strokes%20and%20soft%20gradient%20washes%2C%20hints%20of%20comic%20panels%20and%20speech%20bubbles%20subtly%20blended%2C%20modern%20minimalist%20design%2C%20no%20text%2C%20atmospheric%20lighting%2C%20artistic%20and%20sophisticated&width=1600&height=900&seq=hero-bg-manga-01&orientation=landscape"
        alt="韩国漫画在线阅读"
        className="absolute inset-0 w-full h-full object-cover object-center"
      />
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/60"></div>

      <div className="relative z-10 w-full max-w-[1024px] mx-auto px-4 md:px-6 text-center">
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-background-50 mb-4 font-heading leading-tight">
          探索韩漫世界的
          <span className="text-primary-400">无限精彩</span>
        </h1>
        <p className="text-base md:text-lg text-background-200 max-w-2xl mx-auto mb-8 leading-relaxed">
          5000+部韩国漫画免费在线阅读，无删减、下拉式，更新速度领先。随时随地，畅享韩漫。
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
          <Link
            to="/category/all"
            className="px-8 py-3 bg-primary-500 text-background-50 rounded-full font-semibold text-sm hover:bg-primary-600 transition-colors whitespace-nowrap"
          >
            <i className="ri-book-open-line mr-2"></i>
            开始阅读
          </Link>
          <Link
            to="/about"
            className="px-8 py-3 bg-background-50/10 text-background-50 border border-background-50/30 rounded-full font-semibold text-sm hover:bg-background-50/20 transition-colors whitespace-nowrap backdrop-blur-sm"
          >
            了解更多
          </Link>
        </div>

        {/* Stats */}
        <div className="flex items-center justify-center gap-8 md:gap-12 mt-12">
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">5000+</div>
            <div className="text-xs md:text-sm text-background-300 mt-1">漫画资源</div>
          </div>
          <div className="w-px h-10 bg-background-50/20"></div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">12</div>
            <div className="text-xs md:text-sm text-background-300 mt-1">分类频道</div>
          </div>
          <div className="w-px h-10 bg-background-50/20"></div>
          <div className="text-center">
            <div className="text-2xl md:text-3xl font-bold text-background-50 font-heading">日更</div>
            <div className="text-xs md:text-sm text-background-300 mt-1">百部更新</div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 animate-bounce">
        <span className="w-8 h-8 flex items-center justify-center text-background-50/60">
          <i className="ri-arrow-down-s-line text-xl"></i>
        </span>
      </div>
    </section>
  );
}

function CategorySection() {
  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
            分类浏览
          </h2>
          <p className="text-sm text-foreground-500 mt-2">按喜好探索不同类型的韩漫作品</p>
        </div>

        <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-3 md:gap-4">
          {categories.map((cat) => (
            <Link
              key={cat.slug}
              to={cat.slug === "all" ? "/category/all" : `/category/${cat.slug}`}
              className="group flex flex-col items-center gap-2 p-4 md:p-5 rounded-xl bg-background-100 hover:bg-primary-100/50 transition-colors border border-background-200/60 hover:border-primary-200/60"
            >
              <span className="w-10 h-10 md:w-12 md:h-12 flex items-center justify-center rounded-full bg-background-50 group-hover:bg-primary-500 group-hover:text-background-50 text-primary-500 transition-all duration-300">
                <i className={`${cat.icon} text-lg md:text-xl`}></i>
              </span>
              <span className="text-xs md:text-sm font-medium text-foreground-700 group-hover:text-primary-700 transition-colors whitespace-nowrap">
                {cat.name}
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

function FeaturedSection() {
  const featured = comics.filter((c) => c.featured);

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              热门推荐
            </h2>
            <p className="text-sm text-foreground-500 mt-2">千万读者好评的精选韩漫</p>
          </div>
          <Link
            to="/category/all"
            className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap"
          >
            查看全部
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {featured.map((comic) => (
            <ComicCard
              key={comic.id}
              id={comic.id}
              title={comic.title}
              cover={comic.cover}
              author={comic.author}
              status={comic.status}
              rating={comic.rating}
              views={comic.views}
              category={comic.category}
              tags={comic.tags}
              updateDate={comic.updateDate}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function LatestSection() {
  const sorted = [...comics].sort(
    (a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()
  );

  return (
    <section className="py-14 md:py-20 bg-background-50">
      <div className="w-full max-w-[1200px] mx-auto px-4 md:px-6">
        <div className="flex items-center justify-between mb-10">
          <div>
            <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
              最新更新
            </h2>
            <p className="text-sm text-foreground-500 mt-2">实时同步韩国最新连载进度</p>
          </div>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
          {sorted.slice(0, 8).map((comic) => (
            <ComicCard
              key={comic.id}
              id={comic.id}
              title={comic.title}
              cover={comic.cover}
              author={comic.author}
              status={comic.status}
              rating={comic.rating}
              views={comic.views}
              category={comic.category}
              tags={comic.tags}
              updateDate={comic.updateDate}
            />
          ))}
        </div>
      </div>
    </section>
  );
}

function FaqSection() {
  const [openIndex, setOpenIndex] = useState<number | null>(null);

  return (
    <section className="py-14 md:py-20 bg-background-100">
      <div className="w-full max-w-[800px] mx-auto px-4 md:px-6">
        <div className="text-center mb-10">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading">
            常见问题
          </h2>
          <p className="text-sm text-foreground-500 mt-2">关于073漫画网的常见疑问解答</p>
        </div>

        <div className="flex flex-col gap-3">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="rounded-xl bg-background-50 border border-background-200/60 overflow-hidden"
            >
              <button
                onClick={() => setOpenIndex(openIndex === index ? null : index)}
                className="w-full flex items-center justify-between px-5 py-4 text-left"
              >
                <span className="text-sm font-medium text-foreground-800">{faq.question}</span>
                <span className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                  <i
                    className={`text-foreground-400 transition-transform duration-300 ${
                      openIndex === index ? "ri-subtract-line" : "ri-add-line"
                    }`}
                  ></i>
                </span>
              </button>
              {openIndex === index && (
                <div className="px-5 pb-4 text-sm text-foreground-600 leading-relaxed">
                  {faq.answer}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function CTASection() {
  return (
    <section className="py-16 md:py-24 relative overflow-hidden">
      <img
        src="https://readdy.ai/api/search-image?query=A%20warm%20atmospheric%20abstract%20illustration%20with%20soft%20orange%20and%20amber%20gradients%2C%20delicate%20flowing%20shapes%20reminiscent%20of%20ink%20in%20water%2C%20subtle%20paper%20texture%2C%20calm%20and%20inviting%20mood%2C%20minimalist%20composition%2C%20no%20text%2C%20artistic%20fine%20art%20style&width=1600&height=600&seq=cta-bg-manga-02&orientation=landscape"
        alt=""
        className="absolute inset-0 w-full h-full object-cover"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-primary-600/90 to-primary-800/90"></div>

      <div className="relative z-10 w-full max-w-[800px] mx-auto px-4 md:px-6 text-center">
        <h2 className="text-2xl md:text-4xl font-bold text-background-50 font-heading mb-4">
          即刻开启韩漫之旅
        </h2>
        <p className="text-sm md:text-base text-background-200 mb-8 max-w-xl mx-auto">
          无需注册，完全免费。5000+部无删减韩国漫画等你来读，随时随地，下拉即阅。
        </p>
        <Link
          to="/category/all"
          className="inline-flex px-8 py-3 bg-background-50 text-primary-600 rounded-full font-semibold text-sm hover:bg-background-100 transition-colors whitespace-nowrap"
        >
          <i className="ri-play-circle-line mr-2"></i>
          免费开始阅读
        </Link>
      </div>
    </section>
  );
}

export default function Home() {
  return (
    <main>
      {/* JSON-LD Structured Data */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "073漫画网",
            url: "https://073manga.com",
            description:
              "073漫画网推出无删减韩国漫画下拉式免费在线阅读，韩漫资源齐全，更新速度快，让您轻松探索韩漫世界的精彩。",
            inLanguage: "zh-CN",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://073manga.com/search?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "073漫画网",
            url: "https://073manga.com",
            description:
              "专注韩国漫画的免费在线阅读平台，提供无删减下拉式阅读体验。",
            contactPoint: {
              "@type": "ContactPoint",
              email: "contact@073manga.com",
              contactType: "customer service",
              availableLanguage: "Chinese",
            },
          }),
        }}
      />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: faqs.map((faq) => ({
              "@type": "Question",
              name: faq.question,
              acceptedAnswer: {
                "@type": "Answer",
                text: faq.answer,
              },
            })),
          }),
        }}
      />

      <Navbar />
      <HeroSection />
      <CategorySection />
      <FeaturedSection />
      <LatestSection />
      <CTASection />
      <FaqSection />
      <Footer />
    </main>
  );
}